import { t } from "i18next";
import { useContext, useEffect, useMemo, useState } from "react";
import { Clock, MagicStar, Setting2 } from "iconsax-react";
import DBButton from "../../component/global/DBButton";
import { ToastContext } from "../../component/global/ToastContext";
import { useRequest } from "../../hooks/helperData/useRequest";
import { FEEDBACK_AI_SUMMARY_URL } from "../../helperData/ApiAddress";
import {
    AiCoinHint,
    isInsufficientCoins,
    useAiCoinGate,
} from "../../features/ai-billing/AiCoinGate";
import AiInsightBody from "../../features/ai-insight/AiInsightBody";
import MessagesAiSummaryModal from "./MessagesAiSummaryModal";
import MessagesAiSummaryHistoryModal from "./MessagesAiSummaryHistoryModal";
import {
    describeAiSummaryFilters,
    emptyAiSummaryFilters,
    filtersFromSnapshot,
    formatSummaryDateTime,
    toAiSummaryParams,
} from "./aiSummaryFilters";
import { messageFromAiError } from "./aiSummaryError";

export default function MessagesAiSummary({ companyId }) {
    const toast = useContext(ToastContext);
    const [settingsOpen, setSettingsOpen] = useState(false);
    const [historyOpen, setHistoryOpen] = useState(false);
    const [filters, setFilters] = useState(emptyAiSummaryFilters);
    const [summary, setSummary] = useState(null);
    const [getStored] = useRequest(FEEDBACK_AI_SUMMARY_URL, "GET");
    const [generateAiSummary, generating] = useRequest(
        FEEDBACK_AI_SUMMARY_URL,
        "POST"
    );
    const { confirmAndRun, openFromError, gate } = useAiCoinGate();

    useEffect(() => {
        getStored(toAiSummaryParams(emptyAiSummaryFilters(), companyId))
            .then((payload) => {
                if (!payload?.summary && !payload?.headline) {
                    return;
                }
                setSummary(payload);
                if (payload.filter_snapshot) {
                    setFilters(filtersFromSnapshot(payload.filter_snapshot));
                }
            })
            .catch(() => {});
    }, [companyId]);

    const chips = useMemo(
        () => describeAiSummaryFilters(filters, t),
        [filters]
    );
    const hasText = Boolean(summary?.summary || summary?.headline);
    const generatedAt =
        summary?.jalali_generated_at || summary?.generated_at;
    const includedCount =
        summary?.stored_included_count ?? summary?.included_count;
    const matchedCount =
        summary?.stored_matched_count ?? summary?.matched_count;

    const applyPayload = (payload, appliedFilters) => {
        setSummary(payload);
        if (appliedFilters) {
            setFilters(appliedFilters);
        } else if (payload.filter_snapshot) {
            setFilters(filtersFromSnapshot(payload.filter_snapshot));
        }
        setSettingsOpen(false);
    };

    const runGenerate = (nextFilters) => {
        const applied = nextFilters || filters;
        setFilters(applied);
        confirmAndRun("message_summary", () => {
            generateAiSummary({
                ...toAiSummaryParams(applied, companyId),
                filter_snapshot: applied,
            })
                .then((payload) => {
                    if (!payload.matched_count) {
                        toast(
                            t("Organizational.Messages.AiSummary.EmptyMatch"),
                            "info"
                        );
                        return;
                    }
                    applyPayload(payload, applied);
                })
                .catch((e) => {
                    if (isInsufficientCoins(e)) {
                        openFromError(e);
                        return;
                    }
                    const message = messageFromAiError(
                        e,
                        t,
                        "Organizational.Messages.AiSummary.Error"
                    );
                    toast(message, "danger");
                });
        });
    };

    return (
        <section
            className="messages-ai-summary"
            aria-labelledby="messages-ai-summary-title"
        >
            <div className="messages-ai-summary__header">
                <h2 id="messages-ai-summary-title">
                    <MagicStar size={18} aria-hidden="true" />
                    {t("Organizational.Messages.AiSummary.Title")}
                </h2>
                <div className="messages-ai-summary__actions">
                    <DBButton
                        type="secondary"
                        outlined
                        caption={
                            <>
                                <Setting2 size={16} aria-hidden="true" />
                                {t("Organizational.Messages.AiSummary.Settings")}
                            </>
                        }
                        onClick={() => setSettingsOpen(true)}
                    />
                    <DBButton
                        type="secondary"
                        outlined
                        loading={generating}
                        caption={
                            <>
                                <MagicStar size={16} aria-hidden="true" />
                                {hasText
                                    ? t(
                                          "Organizational.Messages.AiSummary.Refresh"
                                      )
                                    : t(
                                          "Organizational.Messages.AiSummary.Generate"
                                      )}
                                <AiCoinHint operation="message_summary" />
                            </>
                        }
                        onClick={() => runGenerate(filters)}
                    />
                    <DBButton
                        type="secondary"
                        outlined
                        className="messages-ai-summary__history"
                        aria-label={t(
                            "Organizational.Messages.AiSummary.History"
                        )}
                        title={t("Organizational.Messages.AiSummary.History")}
                        caption={<Clock size={18} aria-hidden="true" />}
                        onClick={() => setHistoryOpen(true)}
                    />
                </div>
            </div>
            {chips.length > 0 && (
                <ul className="messages-ai-summary__chips">
                    {chips.map((chip) => (
                        <li key={chip.id}>{chip.label}</li>
                    ))}
                </ul>
            )}
            {hasText ? (
                <AiInsightBody
                    headline={summary.headline}
                    summary={summary.summary}
                    highlights={summary.highlights}
                    metaItems={[
                        generatedAt && {
                            id: "generated",
                            text: t(
                                "Organizational.Messages.AiSummary.GeneratedAt",
                                {
                                    date: formatSummaryDateTime(generatedAt),
                                }
                            ),
                        },
                        matchedCount != null && {
                            id: "included",
                            text: t(
                                "Organizational.Messages.AiSummary.Included",
                                {
                                    included: includedCount,
                                    matched: matchedCount,
                                }
                            ),
                        },
                    ]}
                />
            ) : (
                <AiInsightBody tone="empty">
                    <p className="ai-insight__summary">
                        {t("Organizational.Messages.AiSummary.Empty")}
                    </p>
                </AiInsightBody>
            )}
            <MessagesAiSummaryHistoryModal
                show={historyOpen}
                onHide={() => setHistoryOpen(false)}
            />
            <MessagesAiSummaryModal
                show={settingsOpen}
                filters={filters}
                companyId={companyId}
                generating={generating}
                onHide={() => setSettingsOpen(false)}
                onApply={(draft) => {
                    setFilters(draft);
                    setSettingsOpen(false);
                }}
                onGenerate={runGenerate}
            />
            {gate}
        </section>
    );
}
